import {
	createStore,
	createHook,
	createActionsHook,
	createContainer,
} from '@atlassian/react-sweet-state';
import type { ObjectActivityTimelineState, ContainerProps } from '../../common/types/store.tsx';
import * as actions from './actions/index.tsx';

export type Actions = typeof actions;

const initialState: ObjectActivityTimelineState = {
	timelineEvents: null,
	loading: false,
	error: null,
	page: {
		current: 0,
		isLast: false,
	},
	filteredByEventTypes: [],
	allowRelativeTimestamp: true,
	commentBeingEdited: null,
};

const Store = createStore<ObjectActivityTimelineState, Actions>({
	initialState,
	actions,
	name: 'object-timeline-activity-store',
});

const _useObjectTimelineActivityState = createHook(Store);

const getTimelineEvents = ({
	timelineEvents,
	loading,
	error,
	page,
}: ObjectActivityTimelineState) => ({
	timelineEvents,
	loading,
	error,
	page,
});

export const useTimelineActions = createActionsHook(Store);

export const useIsAddingNewComment = createHook(Store, {
	selector: (state) => state.commentBeingEdited === 'NEW',
});

export const useIsEditingComment = createHook(Store, {
	selector: (state, props: { commentId: ObjectActivityTimelineState['commentBeingEdited'] }) =>
		state.commentBeingEdited === props.commentId,
});

export const useTimelineLoadingState = createHook(Store, {
	selector: (state) => state.loading,
});

export const useTimelineEvents = createHook(Store, {
	selector: getTimelineEvents,
});

const getFilteredByEventTypes = ({ filteredByEventTypes }: ObjectActivityTimelineState) =>
	filteredByEventTypes;

export const useFilteredByEventTypes = createHook(Store, {
	selector: getFilteredByEventTypes,
});

const getAllowRelativeTimestamp = ({ allowRelativeTimestamp }: ObjectActivityTimelineState) =>
	Boolean(allowRelativeTimestamp);

export const useAllowRelativeTimestamp = createHook(Store, {
	selector: getAllowRelativeTimestamp,
});

// Store container
export const ObjectActivityTimelineContainer = createContainer<
	ObjectActivityTimelineState,
	Actions,
	ContainerProps
>(Store, {});
